/**
 * Member profile — V5-08, `/people/:userId`.
 *
 * "If I add someone to multiple projects, I have to open each project to see
 * their tasks." This is the answer: one page, every project, grouped.
 *
 * Everything here is scoped to what the *viewer* may see. The API applies that
 * filter to the header counts and the task list alike, so this page never
 * becomes a way to learn that work exists in a project you cannot open.
 */

import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Mail, Users } from 'lucide-react';
import { PageHeader } from '@/app/layout/AppShell';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/ui/avatar';
import { EmptyState } from '@/components/shared/EmptyState';
import { DueChip, PriorityPill } from '@/components/shared/TaskRow';
import { ApiError, api } from '@/lib/api';
import { useTasks, type TaskGroupView, type TaskView } from '@/features/tasks/api';
import { useProjects } from '@/features/projects';
import { WorkloadMeter } from '@/components/shared/WorkloadMeter';

interface WorkloadRow {
  userId: string;
  name: string;
  capacityMinutes: number;
  assignedMinutes: number;
  utilisation: number;
  workingDays: number;
  leaveDays: number;
  taskCount: number;
}

const DAY_MS = 86_400_000;

/** Monday-first, matching the workload page so both show the same week. */
function startOfWeek(date: Date): Date {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d;
}

/**
 * This week's leave-adjusted capacity for one person — V5-08 §2.
 *
 * Reuses `GET /users/:id/workload`, which is the same calculation the workload
 * table runs, rather than recomputing capacity here. Seeing a different number
 * on two screens is worse than seeing it on one.
 *
 * That endpoint is `AUTH` and self-scoped: your own always, someone else's only
 * with `report.viewTeam`. A viewer without it simply does not get the strip.
 */
function useUserWorkload(userId: string | undefined) {
  const from = useMemo(() => startOfWeek(new Date()), []);
  const to = useMemo(() => new Date(from.getTime() + 6 * DAY_MS), [from]);

  const queryResult = useQuery({
    queryKey: ['user-workload', userId, from.toISOString()],
    enabled: Boolean(userId),
    retry: false,
    queryFn: async () =>
      (
        await api.get<WorkloadRow | null>(
          `/users/${userId}/workload?from=${from.toISOString()}&to=${to.toISOString()}`,
        )
      ).data,
  });

  return { ...queryResult, from, to };
}

interface MemberProfileData {
  user: {
    _id: string;
    name: string;
    email: string;
    designation?: string;
    systemRole: string;
    avatarUrl?: string;
    status: string;
    joinedAt?: string;
  };
  teams: { _id: string; name: string }[];
  availability: 'on_leave' | 'half_day' | 'wfh' | null;
  stats: { open: number; overdue: number; dueThisWeek: number; completedLast30Days: number };
}

const AVAILABILITY_LABEL: Record<string, string> = {
  on_leave: 'On leave today',
  half_day: 'Half day',
  wfh: 'Working from home',
};

type StatusFilter = 'open' | 'done' | 'all';
type DueFilter = 'all' | 'overdue' | 'this_week' | 'next_week';

function useMemberProfile(userId: string | undefined) {
  return useQuery({
    queryKey: ['member-profile', userId],
    enabled: Boolean(userId),
    retry: false,
    queryFn: async () => (await api.get<MemberProfileData>(`/users/${userId}/profile`)).data,
  });
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'danger' }) {
  return (
    <div className="vt-surface p-3" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[11px] uppercase tracking-wide vt-muted">{label}</div>
      <div
        className="vt-mono text-[24px] font-semibold"
        style={tone === 'danger' && value > 0 ? { color: 'var(--danger)' } : undefined}
      >
        {value}
      </div>
    </div>
  );
}

export function PersonProfilePage() {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const { data: profile, isLoading, error } = useMemberProfile(userId);
  const { data: workload, from: weekFrom, to: weekTo } = useUserWorkload(userId);
  const { data: projects = [] } = useProjects();

  const [status, setStatus] = useState<StatusFilter>('open');
  const [due, setDue] = useState<DueFilter>('all');
  const [projectId, setProjectId] = useState<string>('all');
  const [search, setSearch] = useState('');

  const filters = useMemo(() => {
    const base: Record<string, unknown> = {
      assigneeId: userId,
      limit: 50,
      // Grouped by project, which is the question this page exists to answer:
      // "what is this person carrying, and where".
      groupBy: 'project',
      // `sortBy`/`dueBucket` are the schema's names, and taskQuerySchema is
      // `.strict()` — an invented key rejects the whole request rather than
      // being ignored.
      sortBy: 'dueDate',
      sortDir: 'asc',
    };
    if (status === 'open') base.statusCategory = 'todo,in_progress';
    if (status === 'done') base.statusCategory = 'done';
    if (projectId !== 'all') base.projectId = projectId;
    if (due !== 'all') base.dueBucket = due;
    if (search.trim()) base.q = search.trim();
    return base;
  }, [userId, status, due, projectId, search]);

  const { data: taskData, isLoading: tasksLoading } = useTasks(filters, { enabled: Boolean(userId) });

  const groups = useMemo<{ key: string; label: string; total: number; tasks: TaskView[] }[]>(() => {
    const items = taskData?.items ?? [];
    if (items.length === 0) return [];

    // `groupBy: 'project'` makes the API return groups. If it ever returns a
    // flat list, fall back to a single group rather than rendering nothing.
    const first = items[0];
    if ('tasks' in first) {
      return (items as TaskGroupView[]).map((g) => ({
        key: g.groupKey,
        label: g.groupLabel,
        total: g.count,
        tasks: g.tasks,
      }));
    }
    return [{ key: 'all', label: 'Tasks', total: items.length, tasks: items as TaskView[] }];
  }, [taskData]);

  const projectName = useMemo(
    () => new Map(projects.map((p) => [p._id, `${p.name} (${p.key})`])),
    [projects],
  );

  if (error instanceof ApiError && error.status === 403) {
    return (
      <>
        <PageHeader title="Profile" />
        <div className="p-4">
          <EmptyState
            icon={<Users className="size-6" />}
            title="Not available"
            description="Member profiles are not available to guest accounts."
          />
        </div>
      </>
    );
  }

  if (isLoading || !profile) {
    return (
      <>
        <PageHeader title="Profile" />
        <div className="p-4">
          <Skeleton className="h-64" />
        </div>
      </>
    );
  }

  const { user, teams, availability, stats } = profile;

  return (
    <>
      <PageHeader title={user.name} />

      <div className="flex flex-col gap-4 p-4" data-testid="person-profile">
        <section className="vt-surface flex flex-wrap items-start gap-3 p-4">
          <UserAvatar id={user._id} name={user.name} src={user.avatarUrl} size={56} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {/* Not an h1: PageHeader already provides the page heading. */}
              <span className="text-[18px] font-semibold" data-testid="profile-name">
                {user.name}
              </span>
              {user.designation && <Badge>{user.designation}</Badge>}
              {user.status !== 'active' && <Badge tone="warn">{user.status}</Badge>}
              {availability && (
                <Badge tone={availability === 'wfh' ? 'accent' : 'warn'} data-testid="availability">
                  {AVAILABILITY_LABEL[availability]}
                </Badge>
              )}
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-3 text-[12px] vt-muted">
              <a
                href={`mailto:${user.email}`}
                className="inline-flex items-center gap-1 hover:text-[var(--accent)]"
              >
                <Mail className="size-3" />
                {user.email}
              </a>
              {user.joinedAt && (
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="size-3" />
                  Joined {new Date(user.joinedAt).toLocaleDateString()}
                </span>
              )}
              {teams.length > 0 && (
                <span className="inline-flex items-center gap-1" data-testid="profile-teams">
                  <Users className="size-3" />
                  {teams.map((t) => t.name).join(', ')}
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Open" value={stats.open} />
          <Stat label="Overdue" value={stats.overdue} tone="danger" />
          <Stat label="Due this week" value={stats.dueThisWeek} />
          <Stat label="Done in 30 days" value={stats.completedLast30Days} />
        </section>

        {workload && (
          <section className="vt-surface flex flex-col gap-1.5 p-3" data-testid="workload-strip">
            <div className="flex items-baseline justify-between">
              <h2 className="text-[12px] font-medium uppercase tracking-wide vt-muted">
                This week
              </h2>
              <span className="text-[11px] vt-muted">
                {weekFrom.toLocaleDateString()} – {weekTo.toLocaleDateString()}
              </span>
            </div>
            <WorkloadMeter
              utilisation={workload.utilisation}
              assignedMinutes={workload.assignedMinutes}
              capacityMinutes={workload.capacityMinutes}
              showTotals
            />
            <p className="text-[11px] vt-muted">
              {workload.workingDays} working day{workload.workingDays === 1 ? '' : 's'}
              {workload.leaveDays > 0
                ? ` · ${workload.leaveDays} on leave (capacity already reduced)`
                : ''}
              {` · ${workload.taskCount} task${workload.taskCount === 1 ? '' : 's'} due this week`}
            </p>
          </section>
        )}

        <section className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Status"
            data-testid="filter-status"
            className="vt-surface h-7 rounded-[6px] px-2 text-[12px]"
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
          >
            <option value="open">Open</option>
            <option value="done">Done</option>
            <option value="all">All</option>
          </select>

          <select
            aria-label="Due"
            data-testid="filter-due"
            className="vt-surface h-7 rounded-[6px] px-2 text-[12px]"
            value={due}
            onChange={(e) => setDue(e.target.value as DueFilter)}
          >
            <option value="all">Any due date</option>
            <option value="overdue">Overdue</option>
            <option value="this_week">This week</option>
            <option value="next_week">Next week</option>
          </select>

          <select
            aria-label="Project"
            data-testid="filter-project"
            className="vt-surface h-7 rounded-[6px] px-2 text-[12px]"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="all">All projects</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name}
              </option>
            ))}
          </select>

          <Input
            aria-label="Search tasks"
            data-testid="filter-search"
            placeholder="Search…"
            className="h-7 w-44"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </section>

        {tasksLoading ? (
          <Skeleton className="h-48" />
        ) : groups.length === 0 ? (
          <EmptyState
            icon={<Users className="size-6" />}
            title="Nothing assigned"
            description={`No tasks match these filters in the projects you can see.`}
          />
        ) : (
          <div className="flex flex-col gap-4" data-testid="person-task-groups">
            {groups.map((group) => (
              <section key={group.key} data-testid="project-group" data-project-id={group.key}>
                <header className="mb-1.5 flex items-baseline gap-2">
                  <h2 className="text-[13px] font-medium">
                    {projectName.get(group.key) ?? group.label}
                  </h2>
                  {/* The true total, not how many happen to be loaded. */}
                  <span className="vt-mono text-[11px] vt-muted">{group.total}</span>
                </header>

                <div className="flex flex-col gap-1">
                  {group.tasks.map((task) => (
                    <button
                      key={task._id}
                      type="button"
                      data-testid="person-task-row"
                      onClick={() => navigate(`/tasks/${task.key}`)}
                      className="vt-surface flex items-center gap-2 px-2 py-1.5 text-left hover:border-[var(--accent)]"
                    >
                      <PriorityPill priority={task.priority} />
                      <span className="vt-mono shrink-0 text-[11px] vt-muted">{task.key}</span>
                      <span className="min-w-0 flex-1 truncate text-[13px]">{task.title}</span>
                      <DueChip dueDate={task.dueDate} done={task.statusCategory === 'done'} />
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
